import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './AiPresetImportModal.css';
import {
  fixedDraftCompareSig,
  fixedDraftsCompareSig,
  DEFAULT_FIXED_TIMETABLE_NAME,
  draftMissing,
  isDraftConfirmed,
  liveDrafts,
  loadFixedDrafts,
  newDraftId,
  periodOptions,
  periodsTimeRange,
  scheduleRowsForGrade,
  subscribeFixedDrafts,
  updateFixedDrafts,
  type FixedProgramDraft,
} from '../../utils/fixedProgramDrafts';
import {
  getClassScheduleSettings,
  getProgramColorSettings,
  loadClassScheduleSettingsCache,
  type ClassScheduleSettings,
  type ProgramColorSettings,
} from '../../services/sectionLayoutService';
import { ClassPickerChips, ColorCircles, HoursStepper, PeriodToggleGrid, TitleDate, TitleName } from './FixedProgramFields';
import { seedFixedDraftsFromCachedAnnualCells } from '../../utils/fixedProgramsFromAnnualCells';

/** grades: 클래스 이름에 붙은 학년 숫자(기존 관리 카드의 원형 학년 표시) */
export type BulkClassOption = { id: string; label: string; grades?: string[] };

/** 명시 저장용 핸들: 편집은 표 안에만 두고 save() 때 공유 목록에 씀(→ 연간시간표 셀 반영) */
export type FixedProgramBulkHandle = { save: () => void; discard: () => void; isDirty: () => boolean; getDrafts: () => FixedProgramDraft[] };

/** 편집본을 최신 공유 목록에 합침: 이 표에서 바뀐/추가된 항목만 덮어쓰고, 다른 곳에서 생긴 항목은 유지 */
function mergeEdits(latest: FixedProgramDraft[], base: FixedProgramDraft[], edited: FixedProgramDraft[]): FixedProgramDraft[] {
  const baseById = new Map(base.map((d) => [d.id, fixedDraftCompareSig(d)]));
  /** 되돌린 항목(정규화 서명이 저장본과 같음)은 다시 쓰지 않는다 */
  const changed = new Map(edited.filter((d) => baseById.get(d.id) !== fixedDraftCompareSig(d) && !(d.deleted && !baseById.has(d.id))).map((d) => [d.id, d] as const));
  const out = latest.map((d) => changed.get(d.id) ?? d);
  const have = new Set(latest.map((d) => d.id));
  changed.forEach((d, id) => {
    if (!have.has(id)) out.push(d);
  });
  return out;
}

/** 수업시간 설정이 없을 때: 1~6교시 + 방과후1~4 */
const FALLBACK_PERIODS = [
  ...[1, 2, 3, 4, 5, 6].map((n) => ({ key: `class:${n}`, label: `${n}교시`, start: '', end: '' })),
  ...[1, 2, 3, 4].map((n) => ({ key: `after_school:${n}`, label: `방과후${n}`, start: '', end: '' })),
];

/**
 * 고정(행사) 프로그램 일괄 관리 표 — 학사일정 하단 · 연간시간표 '고정 프로그램 일괄 관리' · 기존 관리 목록이 같은 데이터를 편집.
 * filterGradeIds 가 있으면 그 클래스에 적용되는 항목(+ 적용클래스 미정 항목)만 표시.
 * includeDateRange 가 있으면 그 학년도 날짜의 행사도 함께 표시한다(1·2학기).
 */
type BulkProps = {
  workspaceId: string;
  classOptions: BulkClassOption[];
  filterGradeIds?: string[];
  /** 학사일정 섹션의 학년도 시작·종료. strictSectionClasses 가 아니면 이 안의 행사도 함께 표시한다. */
  includeDateRange?: { start: string; end: string };
  /** 학사일정 섹션: 학년도 날짜만 맞아도 다른 클래스 행사를 넣지 않고, 이 섹션 클래스가 적용된 항목만 본다. */
  strictSectionClasses?: boolean;
  /** 비어 있으면 섹션 등록 클래스 전체. 있으면 그중 고른 적용클래스만 */
  activeClassIds?: string[];
  emptyText?: string;
  /** 스크린샷·개발용 */
  initialDrafts?: FixedProgramDraft[];
  initialSchedule?: ClassScheduleSettings | null;
  initialColors?: ProgramColorSettings | null;
  /** 스크린샷용: 이 항목의 적용클래스 선택 창을 연 채로 */
  openClassPickerFor?: string;
  /** 저장 안 한 변경 여부 알림 */
  onDirtyChange?: (dirty: boolean) => void;
  /** 연간시간표 관리 모달은 셀에서 맞춘 목록을 그대로 본다. 캐시 시드는 그 목록을 옛 셀로 덮을 수 있다. */
  seedFromCellCache?: boolean;
};
const FixedProgramBulkTable = forwardRef<FixedProgramBulkHandle, BulkProps>(function FixedProgramBulkTable(
  { workspaceId, classOptions, filterGradeIds, includeDateRange, strictSectionClasses = false, activeClassIds, emptyText, initialDrafts, initialSchedule, initialColors, openClassPickerFor, onDirtyChange, seedFromCellCache = true },
  ref
) {
  const [colorSettings, setColorSettings] = useState<ProgramColorSettings | null>(initialColors ?? null);
  const [drafts, setDrafts] = useState<FixedProgramDraft[]>(() => initialDrafts ?? loadFixedDrafts(workspaceId));
  /** 마지막으로 불러온/저장한 공유 목록(변경 비교 기준) */
  const [base, setBase] = useState<FixedProgramDraft[]>(() => initialDrafts ?? loadFixedDrafts(workspaceId));
  /** 저장본 스냅샷과 정규화 비교(고정 플래그가 아님) — 수정 후 되돌리면 다시 '변경사항 없음' */
  const dirty = useMemo(() => fixedDraftsCompareSig(drafts) !== fixedDraftsCompareSig(base), [drafts, base]);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const [schedule, setSchedule] = useState<ClassScheduleSettings | null>(() => initialSchedule ?? loadClassScheduleSettingsCache(workspaceId));

  useLayoutEffect(() => {
    if (initialDrafts || !seedFromCellCache) return;
    seedFixedDraftsFromCachedAnnualCells(workspaceId);
    if (dirtyRef.current) return;
    const fresh = loadFixedDrafts(workspaceId);
    setDrafts(fresh);
    setBase(fresh);
  }, [workspaceId, initialDrafts, seedFromCellCache]);

  useEffect(() => {
    let alive = true;
    void Promise.all([
      getClassScheduleSettings(workspaceId).catch(() => null),
      getProgramColorSettings(workspaceId).catch(() => null),
    ]).then(([scheduleSettings, colorPalette]) => {
      if (!alive) return;
      if (scheduleSettings) setSchedule(scheduleSettings);
      if (colorPalette) setColorSettings(colorPalette);
      if (initialDrafts || !seedFromCellCache) return;
      seedFixedDraftsFromCachedAnnualCells(workspaceId);
      if (dirtyRef.current) return;
      const next = loadFixedDrafts(workspaceId);
      setDrafts(next);
      setBase(next);
    });
    if (initialDrafts) return () => {
      alive = false;
    };
    const off = subscribeFixedDrafts(workspaceId, () => {
      if (!alive || dirtyRef.current) return;
      const next = loadFixedDrafts(workspaceId);
      setDrafts(next);
      setBase(next);
    });
    return () => {
      alive = false;
      off();
    };
  }, [workspaceId, initialDrafts, seedFromCellCache]);

  const visible = useMemo(() => {
    const sectionIds = filterGradeIds && filterGradeIds.length > 0 ? filterGradeIds : [];
    const picked = activeClassIds && activeClassIds.length > 0 ? activeClassIds : null;
    const pool = picked ?? (strictSectionClasses ? sectionIds : null);
    const f = pool && pool.length > 0 ? new Set(pool) : filterGradeIds && filterGradeIds.length > 0 && !strictSectionClasses ? new Set(filterGradeIds) : null;
    const rangeStart = includeDateRange?.start ?? '';
    const rangeEnd = includeDateRange?.end ?? '';
    const inSectionYear = (date: string) => !strictSectionClasses && !!rangeStart && !!rangeEnd && !!date && date >= rangeStart && date <= rangeEnd;
    const matchesClass = (d: FixedProgramDraft, ids: Set<string>) =>
      d.gradeIds.some((g) => ids.has(g)) || Object.keys(d.applied ?? {}).some((g) => ids.has(g));
    return liveDrafts(drafts)
      .filter((d) => {
        if (strictSectionClasses) {
          if (!f) return d.gradeIds.length === 0 && !picked;
          if (picked) return matchesClass(d, f);
          if (d.gradeIds.length === 0) return true;
          return matchesClass(d, f);
        }
        if (!f) return true;
        if (d.gradeIds.length === 0 || d.gradeIds.some((g) => f.has(g))) return true;
        if (Object.keys(d.applied ?? {}).some((g) => f.has(g))) return true;
        return inSectionYear(d.date);
      })
      .sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999'));
  }, [drafts, filterGradeIds, includeDateRange?.start, includeDateRange?.end, strictSectionClasses, activeClassIds]);

  const shownClasses = filterGradeIds ? classOptions.filter((c) => filterGradeIds.includes(c.id)) : classOptions;

  const write = (fn: (prev: FixedProgramDraft[]) => FixedProgramDraft[]) => setDrafts((prev) => fn(prev));

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => dirtyRef.current,
      save: () => {
        if (!dirtyRef.current) return;
        if (initialDrafts) {
          setBase(drafts);
          return;
        }
        const next = updateFixedDrafts(workspaceId, (latest) => mergeEdits(latest, base, drafts));
        setDrafts(next);
        setBase(next);
      },
      discard: () => setDrafts(base),
      getDrafts: () => drafts,
    }),
    [drafts, base, workspaceId, initialDrafts]
  );
  const patch = (id: string, p: Partial<FixedProgramDraft>) =>
    write((prev) => prev.map((d) => (d.id === id ? { ...d, ...p, updatedAt: Date.now() } : d)));
  /** 삭제는 묘비로 남겨 다른 기기·연간시간표 셀 정리에 전달 */
  const remove = (id: string) => write((prev) => prev.map((d) => (d.id === id ? { ...d, deleted: true, updatedAt: Date.now() } : d)));
  const add = () =>
    write((prev) => [
      ...prev,
      {
        id: newDraftId(),
        date: '',
        name: '',
        periods: [],
        timetableName: '',
        startTime: '',
        endTime: '',
        gradeIds: shownClasses.length === 1 ? [shownClasses[0].id] : [],
        source: 'manual',
        updatedAt: Date.now(),
      },
    ]);

  /** 기존 행사 등록 모달과 같은 고정 팔레트(클래스별 '고정' 색) */
  const paletteFor = (gradeId: string): string[] => {
    const raw = colorSettings?.colorsByGrade?.[gradeId]?.regular ?? colorSettings?.workspaceFallback?.regular ?? [];
    const seen = new Set<string>();
    const out = raw.map((c) => (c ?? '').trim()).filter((c) => c && !seen.has(c.toLowerCase()) && !!seen.add(c.toLowerCase()));
    return out.length ? out : ['#2e7d32'];
  };

  const confirmedCount = visible.filter(isDraftConfirmed).length;

  return (
    <div className="cp-fpb">
      <div className="cp-fpb-summary">
        <span className="cp-aip-state is-ok">확정 {confirmedCount}</span>
        <span className="cp-aip-state is-draft">미확정 {visible.length - confirmedCount}</span>
        <span className="cp-aip-sub">날짜·이름·교시·적용클래스가 모두 있으면 확정되어 연간시간표에 반영돼요. 미확정은 이 표에서만 관리돼요.</span>
      </div>
      {visible.length === 0 ? (
        <p className="cp-aip-sub cp-fpb-empty">{emptyText ?? '등록된 고정(행사) 프로그램이 없어요.'}</p>
      ) : (
        <div className="cp-fpb-scroll">
          <div className="cp-fpb-grid" role="table" aria-label="고정 프로그램 목록">
            <div className="cp-fpb-head" role="row">
              <span role="columnheader">날짜 · 행사명</span>
              <span role="columnheader">교시 · 시간표명</span>
              <span role="columnheader">색상 · 편성시간</span>
              <span role="columnheader">적용클래스</span>
              <span role="columnheader">상태</span>
              <span role="columnheader" aria-label="삭제" />
            </div>
            {visible.map((d) => {
              const miss = draftMissing(d);
              const ok = isDraftConfirmed(d);
              const refGrade = d.gradeIds[0] ?? shownClasses[0]?.id ?? '';
              const opts = periodOptions(scheduleRowsForGrade(schedule, refGrade));
              const range = d.periods.length ? periodsTimeRange(d.periods, opts) : d.startTime && d.endTime ? `${d.startTime}~${d.endTime}` : '';
              const palette = paletteFor(refGrade);
              return (
                <div key={d.id} role="row" className={`cp-fpb-item${ok ? '' : ' is-draft'}`}>
                  <div className="cp-fpb-cell cp-fpb-c1">
                    <TitleDate value={d.date} missing={miss.date} onChange={(date) => patch(d.id, { date })} />
                    <TitleName value={d.name} missing={miss.name} onChange={(name) => patch(d.id, { name })} />
                  </div>
                  <div className={`cp-fpb-cell cp-fpb-c2${miss.periods ? ' is-miss' : ''}`}>
                    <PeriodToggleGrid
                      draft={d}
                      options={opts.length ? opts : FALLBACK_PERIODS}
                      namePlaceholder={DEFAULT_FIXED_TIMETABLE_NAME}
                      onChange={(p) => patch(d.id, p)}
                    />
                  </div>
                  <div className="cp-fpb-cell cp-fpb-c3">
                    <div className="cp-fpb-line">
                      <ColorCircles colors={palette} value={d.colorHex ?? ''} onChange={(colorHex) => patch(d.id, { colorHex })} />
                    </div>
                    <div className="cp-fpb-line">
                      <HoursStepper draft={d} range={range} onChange={(p) => patch(d.id, p)} />
                    </div>
                  </div>
                  <div className="cp-fpb-cell cp-fpb-c4">
                    <ClassPickerChips
                      options={shownClasses}
                      value={d.gradeIds}
                      missing={miss.grades}
                      defaultOpen={openClassPickerFor === d.id}
                      onChange={(gradeIds) => patch(d.id, { gradeIds })}
                    />
                  </div>
                  <div className="cp-fpb-cell cp-fpb-c5">
                    <span className={`cp-aip-state ${ok ? 'is-ok' : 'is-draft'}`}>{ok ? '확정' : '미확정'}</span>
                  </div>
                  <div className="cp-fpb-cell cp-fpb-c6">
                    <button type="button" className="cp-aip-del" aria-label="삭제" onClick={() => remove(d.id)}>
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <button type="button" className="cp-aip-add cp-aip-add--full" onClick={add}>
        ＋ 고정 프로그램 추가
      </button>
    </div>
  );
});

export default FixedProgramBulkTable;
